/**
 * Climate (M21): the zone atmosphere draw, SHARED between renderer and
 * generator. drawZone is a pure label-derived function of (seed, zone),
 * which makes weather a legal generation input — the same climate the
 * sky presents can bias the planner's hazard appetites (snowdrift zones
 * grow icy patches, goldhour zones lean into jumps) without any client
 * state ever touching generation. The biome bias stays a PRESENTATION
 * lean only (the client passes it; the generator always reads the BASE
 * draw — islands are planned inside the zone, so the base draw is the
 * only self-consistent generation input).
 *
 * Moved verbatim from src/client/atmosphere.ts — the draw SHAPE (mood
 * weighted → fogNear → fogFar → sunIntensity → weather pick) must never
 * change silently: skies are part of every shared seed.
 */
import { rngFamily } from "./rng.ts";

export type WeatherKind = "none" | "snow" | "dust" | "embers";

/** A zone is this many segments (~2 minutes of travel). */
export const ZONE_SEGMENTS = 5;

export interface MoodSpec {
  name: string;
  weight: number;
  sky: number;
  fogNear: number;
  fogFar: number;
  sunColor: number;
  sunIntensity: number;
  /** Sun offset direction (normalized against focus radius). */
  sunDir: readonly [number, number, number];
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  starColor: number;
  starOpacity: number;
  weathers: readonly WeatherKind[];
  weatherColor?: number;
}

export const MOODS: readonly MoodSpec[] = [
  {
    name: "midnight",
    weight: 3,
    sky: 0x070b16,
    fogNear: 32,
    fogFar: 115,
    sunColor: 0xfff4e0,
    sunIntensity: 2.2,
    sunDir: [0.6, 1.2, 0.4],
    hemiSky: 0xbfd8ff,
    hemiGround: 0x8a6f5a,
    hemiIntensity: 1.1,
    starColor: 0xc7d5ff,
    starOpacity: 0.85,
    weathers: ["none", "dust"],
  },
  {
    name: "ember",
    weight: 2,
    sky: 0x170a08,
    fogNear: 26,
    fogFar: 95,
    sunColor: 0xffab6e,
    sunIntensity: 2.6,
    sunDir: [1.1, 0.9, 0.5],
    hemiSky: 0xffc9a3,
    hemiGround: 0x5a3a30,
    hemiIntensity: 0.9,
    starColor: 0xffd9b0,
    starOpacity: 0.7,
    weathers: ["embers"],
    weatherColor: 0xffa050,
  },
  {
    name: "violet",
    weight: 2,
    sky: 0x0d0a1e,
    fogNear: 30,
    fogFar: 110,
    sunColor: 0xd9c8ff,
    sunIntensity: 2.0,
    sunDir: [0.5, 1.4, 0.7],
    hemiSky: 0x9f8fdf,
    hemiGround: 0x3a3050,
    hemiIntensity: 1.2,
    starColor: 0xe0d5ff,
    starOpacity: 0.95,
    weathers: ["dust", "none"],
    weatherColor: 0xb8a8ff,
  },
  {
    name: "abyss",
    weight: 1.5,
    sky: 0x05100f,
    fogNear: 20,
    fogFar: 72,
    sunColor: 0x9fe8d8,
    sunIntensity: 1.7,
    sunDir: [0.4, 1.0, 0.8],
    hemiSky: 0x7fd8c8,
    hemiGround: 0x1f4038,
    hemiIntensity: 1.0,
    starColor: 0xafeade,
    starOpacity: 0.3,
    weathers: ["none"],
  },
  {
    name: "snowdrift",
    weight: 2,
    sky: 0x0e141f,
    fogNear: 26,
    fogFar: 98,
    sunColor: 0xdfeaff,
    sunIntensity: 2.3,
    sunDir: [0.7, 1.5, 0.3],
    hemiSky: 0xcfe0ff,
    hemiGround: 0x60707f,
    hemiIntensity: 1.3,
    starColor: 0xffffff,
    starOpacity: 0.9,
    weathers: ["snow"],
    weatherColor: 0xf0f6ff,
  },
  {
    name: "goldhour",
    weight: 1.5,
    sky: 0x140f08,
    fogNear: 34,
    fogFar: 125,
    sunColor: 0xffc46a,
    sunIntensity: 2.9,
    sunDir: [2.0, 0.55, 1.2],
    hemiSky: 0xffe0b0,
    hemiGround: 0x504030,
    hemiIntensity: 0.8,
    starColor: 0xffe9c0,
    starOpacity: 0.45,
    weathers: ["none", "dust"],
    weatherColor: 0xd8b890,
  },
];

/** Mood-weight multipliers per island biome (M18a): a themed stretch
 * leans the sky toward its climate WITHOUT ever forcing it — no bias, no
 * change (today's draw exactly). Presentation-only; never touches
 * generation. */
export const BIOME_MOOD_BIAS: Record<string, Record<string, number>> = {
  winter: { snowdrift: 4, abyss: 1.5 },
  spooky: { midnight: 3, abyss: 3 },
  tropical: { goldhour: 3, ember: 1.5 },
  autumn: { ember: 2.5, goldhour: 2.5 },
};

/** One drawn zone: mood + jitter + weather pick. */
export interface ZoneAtmosphere {
  mood: MoodSpec;
  fogNear: number;
  fogFar: number;
  sunIntensity: number;
  weather: WeatherKind;
}

/** Deterministic zone draw — `atmosphere` family, keyed by zone index.
 * `biomeBias` (the zone's dominant island biome, if any) multiplies mood
 * weights before the draw; the rng stream shape is IDENTICAL either way
 * (same draws in the same order — only the weighted outcome moves). */
export function drawZone(
  seed: string,
  zone: number,
  biomeBias?: string,
): ZoneAtmosphere {
  const rng = rngFamily(seed, "atmosphere").child(`zone/${zone}`);
  const bias = biomeBias ? BIOME_MOOD_BIAS[biomeBias] : undefined;
  const mood = rng.weighted(
    MOODS.map((m) => [m, m.weight * (bias?.[m.name] ?? 1)] as const),
  );
  return {
    mood,
    fogNear: mood.fogNear * (0.9 + rng.float() * 0.25),
    fogFar: mood.fogFar * (0.85 + rng.float() * 0.3),
    sunIntensity: mood.sunIntensity * (0.9 + rng.float() * 0.2),
    weather: rng.pick(mood.weathers),
  };
}

/** The BASE climate the generator reads for a segment (M21) — always
 * unbiased: the biome lean is presentation, and islands are planned
 * inside the zone, so the base draw is the only self-consistent
 * generation input. */
export function segmentClimate(
  seed: string,
  segIndex: number,
): { mood: string; weather: WeatherKind } {
  const za = drawZone(seed, Math.floor(segIndex / ZONE_SEGMENTS));
  return { mood: za.mood.name, weather: za.weather };
}
